import FiberManualRecordIcon from "@mui/icons-material/FiberManualRecord";
import {Box, CircularProgress, Divider, Typography, useTheme} from "@mui/material";
import GroupIcon from "@mui/icons-material/Group";
import EmailIcon from "@mui/icons-material/Email";
import WorkIcon from "@mui/icons-material/Work";
import {useEffect, useRef, useState} from "react";
import KpiGroupCard from "../components/ui/KpiGroupCard/KpiGroupCard";
import Window from "../components/Window/Window";
import {useIdentityAuth} from "../context/IdentityAuthContext";
import useDashboardKpis from "../hooks/useDashboardKpis";
import packageJson from "../../package.json";

// Version del panel
const panelVersion = packageJson.version;

// Colores de icono/fondo tomados de theme.palette.colors (varían según el modo claro/oscuro)
const getColorVariant = (key, colors) => ({iconColor: colors[key], iconBg: colors[`${key}BG`]});

// Icono y color de cada grupo de KPIs devuelto por useDashboardKpis
const GROUP_DATA = {
  users: {icon: GroupIcon, colorKey: "blue"},
  tenants: {icon: WorkIcon, colorKey: "pink"},
  tickets: {icon: EmailIcon, colorKey: "orange"}
};

// DATOS PLACEHOLDER DEL GRAFICO
/*const ACTIVITY_DATASET = [
  {day: "11 may", accesos: 15, tenants: 35, usuarios: 20},
  {day: "12 may", accesos: 20, tenants: 58, usuarios: 40},
  {day: "13 may", accesos: 18, tenants: 45, usuarios: 30},
  {day: "14 may", accesos: 28, tenants: 68, usuarios: 48},
  {day: "15 may", accesos: 18, tenants: 48, usuarios: 33},
  {day: "17 may", accesos: 35, tenants: 78, usuarios: 55}
];*/

const SUMMARY_DATA = [
  {groupKey: "users", itemKey: "total", icon: GroupIcon, colorKey: "blue", label: "Usuarios totales"},
  {groupKey: "tenants", itemKey: "active", icon: WorkIcon, colorKey: "pink", label: "Tenants activos"},
  {groupKey: "tickets", itemKey: "total", icon: EmailIcon, colorKey: "orange", label: "Tickets vigentes"}
];

// Busca el valor de un item concreto dentro de los grupos que devuelve useDashboardKpis
const findKpiValue = (groups, groupKey, itemKey) =>
  groups.find((group) => group.key === groupKey)?.items.find((item) => item.key === itemKey)?.value;

const Dashboard = () => {
  const theme = useTheme();
  const {user} = useIdentityAuth();
  const {groups: kpiGroups, loading: kpisLoading} = useDashboardKpis();
  const [healthStatus, setHealthStatus] = useState("checking");
  const [showVersion, setShowVersion] = useState(false);
  const versionTimeoutRef = useRef(null);

  useEffect(() => () => clearTimeout(versionTimeoutRef.current), []);

  const handleVersionReveal = (event) => {
    if (!event.shiftKey) return;
    setShowVersion(true);
    clearTimeout(versionTimeoutRef.current);
    versionTimeoutRef.current = setTimeout(() => setShowVersion(false), 4000);
  };

  useEffect(() => {
    checkHealth();

    const interval = setInterval(() => {
      checkHealth();
    }, 60000);
    return () => clearInterval(interval);
    // eslint-disable-next-line
  }, []);

  const checkHealth = async () => {
    const apiUrl = process.env.REACT_APP_API_AUTH;
    try {
      const response = await fetch(`${apiUrl}/healthz`);
      if (response.ok) {
        setHealthStatus("online");
      } else {
        setHealthStatus("offline");
      }
    } catch (error) {
      setHealthStatus("offline");
    }
  };

  return (
    <Window title={"Dashboard"}>
      <Box sx={{mb: 4}}>
        <Typography variant="h2" sx={{mb: 0.5}} fontWeight="bold" color={theme.palette.text.title}>
          {user?.name ? `¡Bienvenido, ${user.name}!` : "¡Bienvenido!"}
        </Typography>
        <Typography variant="body2" color={theme.palette.text.secondary}>
          Aquí tienes el resumen general
        </Typography>
        {/* <Typography variant="body2" color={theme.palette.text.secondary}>
          KPIs groups: {kpiGroups.map(group => group.title).join(", ")}
        </Typography> */}
      </Box>

      {kpisLoading && <CircularProgress />}
      {!kpisLoading && (
        <>
          <Box
            sx={{
              display: "grid",
              gridTemplateColumns: {
                xs: "1fr",
                sm: "1fr",
                md: "repeat(3, minmax(0, 340px))"
              },
              justifyContent: {xs: "stretch", md: "center"},
              gap: {xs: 2, sm: 3},
              mb: {xs: 2, sm: 3}
            }}
          >
            {kpiGroups.map((group) => {
              const data = GROUP_DATA[group.key] || {icon: GroupIcon, colorKey: "blue"};
              const totalItem = group.items.find((item) => item.key === "total");
              const restItems = group.items.filter((item) => item.key !== "total");
              // En tickets se repite el total vigente como tercer dato de la lista
              const displayItems = group.key === "tickets" && totalItem ? [...restItems, totalItem] : restItems;

              return (
                <KpiGroupCard
                  key={group.key}
                  icon={data.icon}
                  title={group.title}
                  total={totalItem?.value}
                  items={displayItems}
                  denied={!group.allowed}
                  {...getColorVariant(data.colorKey, theme.palette.colors)}
                />
              );
            })}
          </Box>

          <Box
            sx={{
              display: "flex",
              flexWrap: "wrap",
              justifyContent: "center",
              gap: {xs: 2, sm: 3}
            }}
          >
            <Box
              sx={{
                flex: "2 1 500px",
                maxWidth: {xs: "100%", md: 650},
                p: 3,
                backgroundColor: theme.palette.card.background,
                border: `1px solid ${theme.palette.card.border}`,
                borderRadius: theme.palette.card.borderRadius,
                boxShadow: theme.palette.card.borderShadow
              }}
            >
              <Typography variant="body2" fontWeight="bold" color={theme.palette.text.primary} sx={{mb: 1}}>
                Evolución de registros
              </Typography>
              {/* <LineChart
                dataset={ACTIVITY_DATASET}
                xAxis={[{dataKey: "day", scaleType: "band"}]}
                series={[
                  {dataKey: "accesos", label: "Accesos solicitados", color: "#27AE60"},
                  {dataKey: "tenants", label: "Tenants registrados", color: "#EB5292"},
                  {dataKey: "usuarios", label: "Usuarios registrados", color: "#F2994A"}
                ]}
                height={300}
              /> */}
              <Box
                sx={{
                  height: 300,
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center"
                }}
              >
                <Typography variant="body2" color={theme.palette.text.secondary}>
                  Más información pronto...
                </Typography>
              </Box>
            </Box>

            <Box
              sx={{
                flex: "1 1 280px",
                maxWidth: {xs: "100%", md: 450, lg: 295},
                p: 3,
                backgroundColor: theme.palette.card.background,
                border: `1px solid ${theme.palette.card.border}`,
                borderRadius: theme.palette.card.borderRadius,
                boxShadow: theme.palette.card.borderShadow
              }}
            >
              <Typography variant="body2" fontWeight="bold" color={theme.palette.text.primary} sx={{mb: 2}}>
                Resumen rápido
              </Typography>
              {SUMMARY_DATA.map(({groupKey, itemKey, icon: Icon, colorKey, label}, index) => (
                <Box key={label}>
                  <Box sx={{display: "flex", alignItems: "center", gap: 2, py: 1.5}}>
                    <Box
                      sx={{
                        width: 36,
                        height: 36,
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        borderRadius: "8px",
                        backgroundColor: getColorVariant(colorKey, theme.palette.colors).iconBg,
                        flexShrink: 0
                      }}
                    >
                      <Icon sx={{color: getColorVariant(colorKey, theme.palette.colors).iconColor, fontSize: 20}} />
                    </Box>
                    <Typography variant="body2" color={theme.palette.text.primary} sx={{flexGrow: 1}}>
                      {label}
                    </Typography>
                    <Typography variant="subtitle1" fontWeight="bold" color={theme.palette.text.primary}>
                      {findKpiValue(kpiGroups, groupKey, itemKey) ?? "—"}
                    </Typography>
                  </Box>
                  {index < SUMMARY_DATA.length - 1 && <Divider />}
                </Box>
              ))}
            </Box>
          </Box>

          <Box
            sx={{
              position: "fixed",
              bottom: 18,
              right: 18,
              zIndex: 999,
              display: {xs: "none", sm: "block"}
            }}
          >
            {showVersion && (
              <Typography
                variant="caption"
                sx={{
                  position: "absolute",
                  bottom: "100%",
                  right: 0,
                  mb: 1,
                  px: 1.5,
                  py: 0.5,
                  whiteSpace: "nowrap",
                  userSelect: "none",
                  backgroundColor: theme.palette.card.background,
                  border: `1px solid ${theme.palette.card.border}`,
                  borderRadius: theme.palette.card.borderRadius,
                  boxShadow: theme.palette.card.borderShadow,
                  color: theme.palette.text.secondary
                }}
              >
                v{panelVersion}
              </Typography>
            )}
            <Box
              onDoubleClick={handleVersionReveal}
              sx={{
                backgroundColor: theme.palette.card.background,
                border: `1px solid ${theme.palette.card.border}`,
                borderRadius: theme.palette.card.borderRadius,
                boxShadow: theme.palette.card.borderShadow,
                px: 2,
                py: 1,
                userSelect: "none"
              }}
            >
              <Typography variant="caption" color={theme.palette.text.secondary}>
                <FiberManualRecordIcon
                  sx={{
                    fontSize: 10,
                    verticalAlign: "middle",
                    mr: 0.5,
                    color:
                      healthStatus === "online" ? "success.main" : healthStatus === "offline" ? "error.main" : "grey.500"
                  }}
                />
                {healthStatus === "online"
                  ? "Identity API Online"
                  : healthStatus === "offline"
                    ? "Identity API Offline"
                    : "Checking..."}
              </Typography>
            </Box>
          </Box>
        </>
      )}
    </Window>
  );
};

export default Dashboard;
